import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { Card } from './liveBoatStyles';

const Hero = styled(Card)`
  position: relative;
  min-height: 220px;
  max-height: 340px;
  aspect-ratio: 21 / 9;

  @media (max-width: 768px) {
    aspect-ratio: 16 / 10;
    min-height: 180px;
  }
`;

const Photo = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Placeholder = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 80% 60% at 50% 40%, rgba(30, 58, 138, 0.35), transparent),
    linear-gradient(135deg, ${theme.colors.background.secondary}, ${theme.colors.background.primary});
`;

const Vignette = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.55) 0%,
    rgba(15, 23, 42, 0.15) 40%,
    rgba(15, 23, 42, 0.75) 100%
  );
  pointer-events: none;
`;

const HeroTop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
`;

const VesselName = styled.div`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.bold};
  color: #fff;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.5);
`;

const SceneTag = styled.span`
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.semibold};
  background: rgba(15, 23, 42, 0.65);
  color: ${theme.colors.secondary.light};
  border: 1px solid rgba(56, 189, 248, 0.35);
  backdrop-filter: blur(6px);
`;

const ChipRow = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.85rem 1.25rem;
`;

const Chip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.45rem 0.75rem;
  border-radius: ${theme.borderRadius.md};
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.25);
  backdrop-filter: blur(8px);
  min-width: 72px;
`;

const ChipLabel = styled.span`
  font-size: 0.62rem;
  font-weight: ${theme.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(203, 213, 225, 0.85);
`;

const ChipValue = styled.span`
  font-size: 0.95rem;
  font-weight: ${theme.fontWeights.bold};
  color: #fff;
`;

const SocRing = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: conic-gradient(
    ${theme.colors.success.main} ${(p) => p.$pct}%,
    rgba(71, 85, 105, 0.6) 0
  );
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SocInner = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: ${theme.fontWeights.bold};
  color: #fff;
`;

const SocChip = styled(Chip)`
  flex-direction: row;
  align-items: center;
  gap: 0.6rem;
`;

const BoatCanvas = ({ imageUrl, vesselName, scene, navFields, houseSoc, roles }) => {
  const solar = roles.solar?.fields?.panelPower?.formatted || '—';
  const sog = navFields?.sog?.formatted || '—';
  const depth = navFields?.depth?.formatted || '—';

  return (
    <Hero>
      {imageUrl ? <Photo src={imageUrl} alt={vesselName || 'Vessel'} /> : <Placeholder />}
      <Vignette />

      <HeroTop>
        {vesselName ? <VesselName>{vesselName}</VesselName> : <span />}
        {scene?.label ? <SceneTag>{scene.label}</SceneTag> : null}
      </HeroTop>

      <ChipRow>
        {houseSoc != null ? (
          <SocChip>
            <SocRing $pct={Math.min(100, Math.max(0, houseSoc))}>
              <SocInner>{houseSoc.toFixed(0)}%</SocInner>
            </SocRing>
            <div>
              <ChipLabel>House</ChipLabel>
              <ChipValue>{roles.houseBattery?.fields?.voltage?.formatted || '—'}</ChipValue>
            </div>
          </SocChip>
        ) : null}
        <Chip>
          <ChipLabel>Solar</ChipLabel>
          <ChipValue>{solar}</ChipValue>
        </Chip>
        <Chip>
          <ChipLabel>SOG</ChipLabel>
          <ChipValue>{sog}</ChipValue>
        </Chip>
        <Chip>
          <ChipLabel>Depth</ChipLabel>
          <ChipValue>{depth}</ChipValue>
        </Chip>
      </ChipRow>
    </Hero>
  );
};

export default BoatCanvas;
